import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Card,
  CardContent,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  SelectChangeEvent,
  Chip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SearchIcon from '@mui/icons-material/Search';
import { formatMarketCap as formatCurrencyMarketCap } from '../utils/formatCurrency';
import SectorComparisonChart from './SectorComparisonChart';
import {
  getMacroSectorComparison,
  getMacroCountrySummary,
  getMacroAvailableSectors,
  getMacroIndustryComparison,
  getMacroAvailableIndustries,
  MacroSectorComparisonResponse,
  MacroCountrySummaryResponse,
  MacroAvailableSectorsResponse,
  MacroIndustryComparisonResponse,
  MacroAvailableIndustriesResponse,
} from '../services/api';
import { bloomberg } from '../theme/bloombergTheme';

const TIMEFRAMES = ['1D', '1W', '1M', '3M', 'YTD', '1Y'];

// Country/market code to currency mapping
const MARKET_CURRENCY: Record<string, string> = {
  sp500: 'USD', nasdaq100: 'USD', nasdaq_composite: 'USD', russell2000: 'USD',
  ftse100: 'GBP', ftse250: 'GBP',
  cac40: 'EUR', dax: 'EUR', ibex35: 'EUR', aex: 'EUR', bel20: 'EUR',
  nikkei225: 'JPY', kospi: 'KRW',
  smi: 'CHF', spi: 'CHF',
  ftse_mib: 'EUR',
};

const formatMarketCap = (value: number, marketCode?: string): string => {
  const currency = (marketCode && MARKET_CURRENCY[marketCode]) || 'USD';
  return formatCurrencyMarketCap(value, currency);
};

const MarketPerformancePanels: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [timeframe, setTimeframe] = useState('1M');

  // Sector tab state
  const [sectorData, setSectorData] = useState<MacroSectorComparisonResponse | null>(null);
  const [countrySummary, setCountrySummary] = useState<MacroCountrySummaryResponse | null>(null);
  const [availableSectors, setAvailableSectors] = useState<MacroAvailableSectorsResponse | null>(null);
  const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
  const [sectorLoading, setSectorLoading] = useState(true);
  const [sectorError, setSectorError] = useState<string | null>(null);

  // Industry tab state
  const [industryData, setIndustryData] = useState<MacroIndustryComparisonResponse | null>(null);
  const [availableIndustries, setAvailableIndustries] = useState<MacroAvailableIndustriesResponse | null>(null);
  const [industrySearch, setIndustrySearch] = useState('');
  const [industryLoading, setIndustryLoading] = useState(false);
  const [industryError, setIndustryError] = useState<string | null>(null);

  // Fetch country summary independently (shown on all tabs)
  const fetchCountrySummary = useCallback(async () => {
    try {
      const result = await getMacroCountrySummary(timeframe);
      setCountrySummary(result);
    } catch (err) {
      console.error('Error fetching country summary:', err);
    }
  }, [timeframe]);

  // Fetch sector data
  const fetchSectorData = useCallback(async () => {
    setSectorLoading(true);
    setSectorError(null);
    try {
      const sectorResult = await getMacroSectorComparison(timeframe, selectedSectors.length > 0 ? selectedSectors : undefined);
      setSectorData(sectorResult);
    } catch (err) {
      console.error('Error fetching macro data:', err);
      setSectorError(err instanceof Error ? err.message : 'Failed to fetch macro data');
    } finally {
      setSectorLoading(false);
    }
  }, [timeframe, selectedSectors]);

  // Fetch industry data
  const fetchIndustryData = useCallback(async () => {
    setIndustryLoading(true);
    setIndustryError(null);
    try {
      const result = await getMacroIndustryComparison(
        timeframe,
        undefined,
        industrySearch || undefined
      );
      setIndustryData(result);
    } catch (err) {
      console.error('Error fetching industry data:', err);
      setIndustryError(err instanceof Error ? err.message : 'Failed to fetch industry data');
    } finally {
      setIndustryLoading(false);
    }
  }, [timeframe, industrySearch]);

  // Fetch available sectors/industries once on mount
  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [sectorsResult, industriesResult] = await Promise.all([
          getMacroAvailableSectors(),
          getMacroAvailableIndustries(),
        ]);
        setAvailableSectors(sectorsResult);
        setAvailableIndustries(industriesResult);
      } catch (err) {
        console.error('Error fetching available sectors/industries:', err);
      }
    };
    fetchMeta();
  }, []);

  // Fetch country summary on every timeframe change
  useEffect(() => {
    fetchCountrySummary();
  }, [fetchCountrySummary]);

  // Fetch sector data when tab is Sectors
  useEffect(() => {
    if (activeTab === 0) {
      fetchSectorData();
    }
  }, [activeTab, fetchSectorData]);

  // Fetch industry data when tab is Industry
  useEffect(() => {
    if (activeTab === 1) {
      fetchIndustryData();
    }
  }, [activeTab, fetchIndustryData]);

  const handleTimeframeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newTimeframe: string | null
  ) => {
    if (newTimeframe) {
      setTimeframe(newTimeframe);
    }
  };

  const handleSectorChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedSectors(typeof value === 'string' ? value.split(',') : value);
  };

  const handleClearSectors = () => {
    setSelectedSectors([]);
  };

  // Debounce industry search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setIndustrySearch(searchInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Group industries by sector for display
  const industriesBySector = useMemo(() => {
    if (!industryData) return {};
    const grouped: { [sector: string]: typeof industryData.industries } = {};
    for (const ind of industryData.industries) {
      const sector = ind.sector || 'Other';
      if (!grouped[sector]) grouped[sector] = [];
      grouped[sector].push(ind);
    }
    return grouped;
  }, [industryData]);

  return (
    <Box>
      {/* Page Header */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="body2" color="text.secondary">
            {activeTab === 0
              ? 'Compare sector performance across global markets'
              : 'Compare industry performance across global markets'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Sector Filter (only on Sectors tab) */}
          {activeTab === 0 && (
            <>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Filter Sectors</InputLabel>
                <Select
                  multiple
                  value={selectedSectors}
                  onChange={handleSectorChange}
                  input={<OutlinedInput label="Filter Sectors" />}
                  renderValue={(selected) =>
                    selected.length === 0 ? 'All Sectors' : `${selected.length} selected`
                  }
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 400,
                      },
                    },
                  }}
                >
                  {availableSectors?.sectors.map((sector) => (
                    <MenuItem key={sector} value={sector}>
                      <Checkbox checked={selectedSectors.indexOf(sector) > -1} size="small" />
                      <ListItemText
                        primary={sector}
                        secondary={
                          availableSectors.sector_coverage[sector]
                            ? `${availableSectors.sector_coverage[sector].length} markets`
                            : undefined
                        }
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedSectors.length > 0 && (
                <Chip
                  label="Clear"
                  size="small"
                  onClick={handleClearSectors}
                  onDelete={handleClearSectors}
                  sx={{ height: 32 }}
                />
              )}
            </>
          )}

          {/* Industry Search (only on Industry tab) */}
          {activeTab === 1 && (
            <TextField
              size="small"
              placeholder="Search industries..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ minWidth: 260 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
          )}

          {/* Timeframe Selector */}
          <ToggleButtonGroup
            value={timeframe}
            exclusive
            onChange={handleTimeframeChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                px: 1.5,
                py: 0.5,
              },
            }}
          >
            {TIMEFRAMES.map((tf) => (
              <ToggleButton key={tf} value={tf}>
                {tf}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Country Summary Cards (shown on both tabs) */}
      {countrySummary && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            mb: 3,
          }}
        >
          {countrySummary.countries.map((country) => (
            <Box
              key={country.market}
              sx={{
                flex: '1 1 140px',
                maxWidth: { xs: 'calc(50% - 8px)', sm: 'calc(25% - 12px)', md: 'calc(14.28% - 12px)' },
                minWidth: 140,
              }}
            >
              <Card
                sx={{
                  backdropFilter: 'blur(10px)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  height: '100%',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3,
                  },
                }}
              >
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="h5" sx={{ lineHeight: 1 }}>
                      {country.flag}
                    </Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {country.market_name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {country.overall_change >= 0 ? (
                          <TrendingUpIcon sx={{ fontSize: 14, color: 'success.main' }} />
                        ) : (
                          <TrendingDownIcon sx={{ fontSize: 14, color: 'error.main' }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            color: country.overall_change >= 0 ? 'success.main' : 'error.main',
                            fontWeight: 600,
                          }}
                        >
                          {country.overall_change >= 0 ? '+' : ''}
                          {country.overall_change.toFixed(2)}%
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {formatMarketCap(country.total_market_cap, country.market)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      )}

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_e, v) => setActiveTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Sectors" />
        <Tab label="Industry" />
      </Tabs>

      {/* ===== Sectors Tab ===== */}
      {activeTab === 0 && (
        <>
          {sectorLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : sectorError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {sectorError}
            </Alert>
          ) : sectorData ? (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              {sectorData.sectors.length > 0 ? (
                sectorData.sectors.map((sector) => (
                  <Box
                    key={sector.sector}
                    sx={{
                      flex: '1 1 calc(50% - 8px)',
                      minWidth: { xs: '100%', md: 'calc(50% - 8px)' },
                      maxWidth: { xs: '100%', md: 'calc(50% - 8px)' },
                    }}
                  >
                    <SectorComparisonChart
                      sector={sector.sector}
                      data={sector.data}
                    />
                  </Box>
                ))
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center', width: '100%' }}>
                  <Typography variant="body1" color="text.secondary">
                    No sector data available
                  </Typography>
                </Paper>
              )}
            </Box>
          ) : null}

          {sectorData && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Data updated: {new Date(sectorData.metadata.generated_at).toLocaleString()} | Timeframe:{' '}
              {sectorData.metadata.timeframe} | Markets: {sectorData.metadata.markets_included.length}
            </Typography>
          )}
        </>
      )}

      {/* ===== Industry Tab ===== */}
      {activeTab === 1 && (
        <>
          {industryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : industryError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {industryError}
            </Alert>
          ) : industryData ? (
            <>
              {industryData.industries.length > 0 ? (
                Object.entries(industriesBySector)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([sectorName, industries]) => (
                    <Box key={sectorName} sx={{ mb: 4 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: bloomberg.text.primary,
                          mb: 2,
                          pb: 1,
                          borderBottom: '2px solid',
                          borderColor: 'primary.main',
                        }}
                      >
                        {sectorName}
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                          sx={{ ml: 1.5 }}
                        >
                          ({industries.length} {industries.length === 1 ? 'industry' : 'industries'})
                        </Typography>
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 2,
                        }}
                      >
                        {industries.map((ind) => (
                          <Box
                            key={ind.industry}
                            sx={{
                              flex: '1 1 calc(50% - 8px)',
                              minWidth: { xs: '100%', md: 'calc(50% - 8px)' },
                              maxWidth: { xs: '100%', md: 'calc(50% - 8px)' },
                            }}
                          >
                            <SectorComparisonChart
                              sector={ind.industry}
                              data={ind.data}
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center', width: '100%' }}>
                  <Typography variant="body1" color="text.secondary">
                    {industrySearch
                      ? `No industries found matching "${industrySearch}"`
                      : 'No industry data available'}
                  </Typography>
                </Paper>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Data updated: {new Date(industryData.metadata.generated_at).toLocaleString()} | Timeframe:{' '}
                {industryData.metadata.timeframe} | Industries: {industryData.metadata.total_industries} |
                Markets: {industryData.metadata.markets_included.length}
              </Typography>
            </>
          ) : null}
        </>
      )}

    </Box>
  );
};

export default MarketPerformancePanels;
